import React, { useEffect, useState, useMemo } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import {
  FaMapMarkedAlt,
  FaPlus,
  FaEdit,
  FaCheckCircle,
  FaTimesCircle,
  FaSyncAlt,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";

const THEME = {
  primary: "#172C57", // rgb(23, 44, 87)
  primaryLight: "rgba(23, 44, 87, 0.08)",
  primaryHover: "#1E3A6E",
  emerald: "#10B981", // Active
  red: "#EF4444",     // Inactive
  blue: "#2563EB",    // Action / Info
};

const AllDistrict = () => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [districtName, setDistrictName] = useState("");
  const [isActive, setIsActive] = useState(1);
  const [editingId, setEditingId] = useState(null); // null = add mode
  const [saving, setSaving] = useState(false);

  const fetchDistricts = async () => {
    setLoading(true);
    try {
      const response = await fetch(SummaryApi.GetAllDisticts.url, {
        method: SummaryApi.GetAllDisticts.method,
      });
      if (!response.ok) throw new Error("Network error");
      const data = await response.json();
      // supports both { data: [...] } and a plain array response
      setDistricts(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      toast.error("Failed to load districts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, []);

  const openAddModal = () => {
    setEditingId(null);
    setDistrictName("");
    setIsActive(1);
    setIsModalOpen(true);
  };

  const openEditModal = (district) => {
    setEditingId(district.district_id);
    setDistrictName(district.district_name);
    setIsActive(Number(district.is_active));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setIsModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!districtName.trim()) {
      toast.warn("Please enter district name");
      return;
    }

    const isEdit = editingId !== null;
    const api = isEdit ? SummaryApi.UpdateDistrict : SummaryApi.CreateDistrict;
    const payload = isEdit
      ? {
        district_id: editingId,
        district_name: districtName.trim(),
        is_active: isActive,
      }
      : { district_name: districtName.trim() };

    setSaving(true);
    try {
      const response = await fetch(api.url, {
        method: api.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok || result?.success === false) {
        toast.error(
          result?.message || `Could not ${isEdit ? "update" : "create"} district`
        );
      } else {
        toast.success(
          result?.message || `District ${isEdit ? "updated" : "created"}`
        );
        setIsModalOpen(false);
        fetchDistricts();
      }
    } catch (err) {
      toast.error(
        `Something went wrong while ${editingId !== null ? "updating" : "creating"
        } district`
      );
    } finally {
      setSaving(false);
    }
  };

  // Summary counts for the top pastel cards
  const stats = useMemo(() => {
    const total = districts.length;
    const active = districts.filter((d) => Number(d.is_active) === 1).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [districts]);

  return (
    <div className="min-h-screen space-y-4 text-slate-800">
      {/* Top Header Card */}
      <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3.5">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-sm shrink-0"
            style={{ backgroundColor: THEME.primary }}
          >
            <FaMapMarkedAlt size={18} />
          </div>
          <div>
            <h1
              className="text-lg font-bold tracking-tight"
              style={{ color: THEME.primary }}
            >
              District Management
            </h1>
            <p className="text-xs text-slate-500">
              Create, update, and manage active and inactive districts.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
          <button
            onClick={fetchDistricts}
            disabled={loading}
            className="px-3 py-2 bg-[#F8FAFC] border border-slate-200 text-slate-700 rounded-lg text-xs font-medium hover:bg-slate-100 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            title="Refresh Districts"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} size={11} />
            Refresh
          </button>

          <button
            onClick={openAddModal}
            style={{ backgroundColor: THEME.primary }}
            className="hover:opacity-90 text-white text-xs font-medium px-4 py-2 rounded-lg shadow-sm transition-opacity flex items-center gap-1.5"
          >
            <FaPlus size={11} />
            Add District
          </button>
        </div>
      </div>

      {/* Pastel KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        <div className="bg-[#F0F5FF] border border-[#DBEAFE] rounded-xl p-4 flex items-center gap-3.5">
          <div className="bg-[#2563EB] w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
            <FaMapMarkedAlt size={16} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600">Total Districts</p>
            <p className="text-2xl font-bold text-slate-900 leading-tight my-0.5">
              {stats.total}
            </p>
            <p className="text-[11px] text-slate-500">All configured districts</p>
          </div>
        </div>

        <div className="bg-[#ECFDF5] border border-[#D1FAE5] rounded-xl p-4 flex items-center gap-3.5">
          <div className="bg-[#10B981] w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
            <FaCheckCircle size={16} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600">Active Districts</p>
            <p className="text-2xl font-bold text-slate-900 leading-tight my-0.5">
              {stats.active}
            </p>
            <p className="text-[11px] text-slate-500">Currently enabled</p>
          </div>
        </div>

        <div className="bg-[#FEF2F2] border border-[#FEE2E2] rounded-xl p-4 flex items-center gap-3.5">
          <div className="bg-[#EF4444] w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
            <FaTimesCircle size={16} />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600">Inactive Districts</p>
            <p className="text-2xl font-bold text-slate-900 leading-tight my-0.5">
              {stats.inactive}
            </p>
            <p className="text-[11px] text-slate-500">Currently disabled</p>
          </div>
        </div>
      </div>

      {/* Districts Table Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <h2 className="text-sm font-bold" style={{ color: THEME.primary }}>
              All Districts List
            </h2>
            <span
              style={{
                backgroundColor: THEME.primaryLight,
                color: THEME.primary,
              }}
              className="text-xs font-bold px-2.5 py-0.5 rounded-full"
            >
              {districts.length} Records
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-left">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 tracking-wide w-20">
                  S.No
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 tracking-wide">
                  District Name
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 tracking-wide">
                  Status
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 tracking-wide">
                  Created Date
                </th>
                <th className="px-5 py-3 text-xs font-semibold text-slate-600 tracking-wide text-center w-32">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-5 py-12 text-center">
                    <FaSpinner
                      className="animate-spin text-2xl mx-auto mb-2"
                      style={{ color: THEME.primary }}
                    />
                    <span className="text-xs text-slate-500 font-medium">
                      Loading districts...
                    </span>
                  </td>
                </tr>
              ) : districts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-5 py-12 text-center">
                    <FaMapMarkedAlt className="text-slate-300 text-3xl mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">
                      No districts found
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Click "+ Add District" to create your first district.
                    </p>
                  </td>
                </tr>
              ) : (
                districts.map((d, i) => {
                  const activeStatus = Number(d.is_active) === 1;
                  return (
                    <tr
                      key={d.district_id || i}
                      className="hover:bg-slate-50/80 transition-colors"
                    >
                      <td className="px-5 py-3 text-xs font-medium text-slate-500">
                        {i + 1}
                      </td>
                      <td className="px-5 py-3 text-xs font-semibold text-slate-900">
                        {d.district_name}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${activeStatus
                            ? "bg-[#ECFDF5] text-[#10B981] border-[#D1FAE5]"
                            : "bg-[#FEF2F2] text-[#EF4444] border-[#FEE2E2]"
                            }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${activeStatus ? "bg-[#10B981]" : "bg-[#EF4444]"
                              }`}
                          />
                          {activeStatus ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600">
                        {d.created_date || "N/A"}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <button
                          onClick={() => openEditModal(d)}
                          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors shadow-sm inline-flex items-center gap-1.5"
                        >
                          <FaEdit size={11} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit District Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        ariaHideApp={false}
        className="bg-white w-full max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden outline-none"
        overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      >
        {/* Modal Header */}
        <div
          style={{ backgroundColor: THEME.primary }}
          className="px-5 py-4 flex justify-between items-center text-white"
        >
          <div className="flex items-center gap-2.5">
            <FaMapMarkedAlt size={15} />
            <h3 className="text-sm font-bold tracking-wide">
              {editingId !== null ? "Edit District" : "Add New District"}
            </h3>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="text-white/80 hover:text-white w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <FaTimes size={13} />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 bg-[#F8FAFC]">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              District Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={districtName}
              onChange={(e) => setDistrictName(e.target.value)}
              placeholder="Enter district name"
              className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#172C57] transition-all"
              autoFocus
            />
          </div>

          {editingId !== null && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Status
              </label>
              <select
                value={isActive}
                onChange={(e) => setIsActive(Number(e.target.value))}
                className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#172C57] cursor-pointer"
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ backgroundColor: THEME.primary }}
              className="px-4 py-1.5 text-white text-xs font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-sm disabled:opacity-60 flex items-center gap-1.5"
              disabled={saving}
            >
              {saving && <FaSpinner className="animate-spin" size={11} />}
              {saving
                ? "Saving..."
                : editingId !== null
                  ? "Update District"
                  : "Save District"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AllDistrict;